import type { FunctionComponent, PropsWithChildren, ReactNode } from 'react';
import cx from 'classnames';

import { Button } from '@trello/nachos/button';
import { UpIcon } from '@trello/nachos/icons/up';

import styles from './PopoverSection.less';

export interface PopoverSectionProps {
  title: ReactNode;
  /**
   * Whether the section can be collapsed. If this is true, a collapse chevron
   * is rendered to the side of the header.
   *
   * For convenience, see {@link usePersistentCollapsiblePopoverSection}.
   * @default false
   */
  isCollapsible?: boolean;
  /**
   * Whether the section is collapsed.
   * @default false
   */
  isCollapsed?: boolean;
  onToggleCollapsed?: () => void;
}

/**
 * EXPERIMENTAL: Our popovers often have a specific style of headers, but we
 * haven't codified this style in a reusable way. This component implements
 * a section of a popover with a header and content.
 *
 * Because Popover and PopoverMenu have conflicting opinions regarding padding,
 * and because PopoverMenu is an unordered list element (meaning it only wants
 * list items as direct descendants), this should be rendered _between_ the two:
 *
 * @example
 * <Popover>
 *   <PopoverSection title="My section">
 *     <PopoverMenu>
 *       <PopoverMenuButton>Click me</PopoverMenuButton>
 *     </PopoverMenu>
 *   </PopoverSection>
 * </Popover>
 */
export const PopoverSection: FunctionComponent<
  PropsWithChildren<PopoverSectionProps>
> = ({ children, title, isCollapsible, isCollapsed, onToggleCollapsed }) => {
  return (
    <section>
      <div className={styles.header}>
        <h3 className={styles.title}>{title}</h3>
        {isCollapsible && (
          <Button
            appearance="subtle"
            className={styles.button}
            iconBefore={
              <UpIcon
                label={isCollapsed ? 'expand section' : 'collapse section'}
                size="small"
                dangerous_className={cx(styles.icon, {
                  [styles.collapsed]: isCollapsed,
                })}
              />
            }
            onClick={onToggleCollapsed}
          />
        )}
      </div>
      <div
        className={cx(styles.content, {
          [styles.collapsed]: isCollapsed,
        })}
        data-testid="popover-section-content"
      >
        {children}
      </div>
    </section>
  );
};
